import { createSlice } from '@reduxjs/toolkit';
import {
  addNewMinerThunk,
  updateConnectedPoolThunk,
  updateMinerThunk
} from './miner.thunks';

const minerSlice = createSlice({
  name: 'miner',
  initialState: {
    minerTypes: [],
    // Set on first successful fetch — miner types are no longer preloaded at app startup (see
    // App.js); the add-miner/add-product forms fetch on demand via useEnsureMinerTypes. The
    // gate also matters because addMinerTypesSuccess appends rather than replaces.
    minerTypesLoaded: false,
    // The full fleet is NO LONGER delivered by the /tenant composite. It is filled on demand
    // by useEnsureAllMiners (pages GET tenant/devices) for the handful of pages that need the
    // whole list for aggregates/lookups; minersLoaded gates that fetch to once per session.
    miners: [],
    minersLoaded: false,
    // Server-side page for the All Devices table (GET tenant/devices).
    devicePage: {
      rows: [],
      totalCount: 0,
      fleetTotal: 0,
      fleetOnline: 0,
      fleetOffline: 0,
      page: 1,
      pageSize: 25,
      isLoading: false,
      error: null
    },
    minerConsumption: [],
    activeMiners: 0,
    inactiveMiners: 0,
    isLoading: false,
    error: null
  },
  reducers: {
    // General loading state for any request
    requestStart: state => {
      state.isLoading = true;
      state.error = null;
    },
    // General failure state for any request
    requestFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    addMinerTypesSuccess: (state, action) => {
      state.isLoading = false;
      state.minerTypes = [...state.minerTypes, action.payload];
      state.minerTypesLoaded = true;
    },
    fetchAllMinersSuccess: (state, action) => {
      state.miners = action.payload;
      state.minersLoaded = true;
    },
    fetchDevicePageStart: state => {
      state.devicePage.isLoading = true;
      state.devicePage.error = null;
    },
    fetchDevicePageSuccess: (state, action) => {
      const {
        devices,
        totalCount,
        fleetTotal,
        fleetOnline,
        fleetOffline,
        page,
        pageSize
      } = action.payload;
      state.devicePage = {
        rows: devices ?? [],
        totalCount: totalCount ?? 0,
        fleetTotal: fleetTotal ?? 0,
        fleetOnline: fleetOnline ?? 0,
        fleetOffline: fleetOffline ?? 0,
        page: page ?? 1,
        pageSize: pageSize ?? 25,
        isLoading: false,
        error: null
      };
      // Keep the headline counts in sync with the fleet-wide numbers the page carries.
      state.activeMiners = fleetOnline ?? state.activeMiners;
      state.inactiveMiners = fleetOffline ?? state.inactiveMiners;
    },
    fetchDevicePageFailure: (state, action) => {
      state.devicePage.isLoading = false;
      state.devicePage.error = action.payload;
    },
    setMinersCount: (state, action) => {
      state.activeMiners = action.payload.activeMiners;
      state.inactiveMiners = action.payload.inactiveMiners;
    },
    fetchMinerConsumptionSuccess: (state, action) => {
      state.minerConsumption = action.payload;
      state.isLoading = false;
    },
    deleteMinerSuccess: (state, action) => {
      const deletedId = action.payload;
      state.miners = state.miners.filter(miner => miner.id !== deletedId);
      state.isLoading = false;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(updateMinerThunk.fulfilled, (state, action) => {
        const { id, updateFieldKey, value, locationName } = action.payload;

        const miner = state.miners.find(miner => miner.id === id);
        if (miner) {
          miner[updateFieldKey] = value;
        }

        if (updateFieldKey === 'locationId' && locationName) {
          miner.previousLocationName = miner.locationName;
          miner.locationName = locationName;
        }
        state.isLoading = false;
      })
      .addCase(updateConnectedPoolThunk.fulfilled, (state, action) => {
        const { id, poolId } = action.payload;

        const miner = state.miners.find(miner => miner.id === id);
        if (miner) {
          miner.poolId = poolId;
        }
        state.isLoading = false;
      })
      .addCase(updateMinerThunk.rejected, (state, action) => {
        console.error('Update failed:', action.payload);
      })
      .addCase(addNewMinerThunk.fulfilled, (state, action) => {
        const { data, formData } = action.payload;

        if (data && formData) {
          state.miners.push({ id: data.MinerId, ...formData });
        }
        state.isLoading = false;
      });
  }
});

export const {
  fetchDevicePageStart,
  fetchDevicePageSuccess,
  fetchDevicePageFailure,
  requestStart,
  requestFailure,
  addMinerTypesSuccess,
  fetchAllMinersSuccess,
  setMinersCount,
  fetchMinerConsumptionSuccess,
  deleteMinerSuccess
} = minerSlice.actions;

export default minerSlice.reducer;
